import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, 
  FaUsers, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaGlobe, 
  FaLock,
  FaUserPlus,
  FaCar,
  FaComments,
  FaSignOutAlt,
  FaCrown,
  FaUserShield,
  FaUser
} from 'react-icons/fa';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import { useToast } from '../hooks/ToastContext';
import Spinner from '../common/Spinner';
import { CustomButton2, CustomButton8 } from '../Templates';
import './styles/GroupsPage.css';
import './styles/GroupDetailsPage.css';

const GroupDetailsPage = () => {
  const { id } = useParams();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [userMember, setUserMember] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  const [groupRides, setGroupRides] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  
  // Fetch group data
  useEffect(() => {
    const fetchGroupData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Fetch group details
        const { data: groupData, error: groupError } = await supabase
          .from('groups')
          .select('*, profiles(*)')
          .eq('id', id)
          .single();
        
        if (groupError) throw groupError;
        
        setGroup(groupData);
        
        // Fetch group members
        const { data: membersData, error: membersError } = await supabase
          .from('group_members')
          .select('*, profiles(*)')
          .eq('group_id', id)
          .eq('status', 'active')
          .order('role', { ascending: false });
        
        if (membersError) throw membersError;
        
        setMembers(membersData || []);
        
        // Check if user is a member
        if (user) {
          const userMemberData = membersData?.find(member => member.user_id === user.id);
          setUserMember(userMemberData || null);
        }
        
        // Fetch group rides
        const { data: ridesData, error: ridesError } = await supabase
          .from('group_rides')
          .select('*, profiles(*)')
          .eq('group_id', id)
          .eq('status', 'active')
          .order('travel_date', { ascending: true });
        
        if (ridesError) throw ridesError;
        
        setGroupRides(ridesData || []);
        
        // Fetch group discussions
        const { data: discussionsData, error: discussionsError } = await supabase
          .from('group_discussions')
          .select('*, profiles(*)')
          .eq('group_id', id)
          .eq('status', 'active')
          .order('created_at', { ascending: false });
        
        if (discussionsError) throw discussionsError;
        
        setDiscussions(discussionsData || []);
        
      } catch (error) {
        console.error('Error fetching group data:', error);
        addToast({
          type: 'error',
          title: t('errors.fetch_error'),
          description: t('errors.group_fetch_failed')
        });
        navigate('/groups');
      } finally {
        setLoading(false);
      }
    };
    
    fetchGroupData();
  }, [id, user, navigate, t, addToast]);

  // Handle joining group
  const handleJoinGroup = async () => {
    if (!user) {
      addToast({
        type: 'error',
        title: t('errors.authentication_required'),
        description: t('errors.login_required_action')
      });
      return;
    }
    
    try {
      // Check if group is public and user can join
      if (group.visibility !== 'public') {
        addToast({
          type: 'error',
          title: t('errors.join_error'),
          description: t('errors.not_public_group')
        });
        return;
      }
      
      // Check if user can join (capacity check)
      const { data: canJoin, error: joinCheckError } = await supabase.rpc(
        'can_join_group',
        { p_group_id: id, p_user_id: user.id }
      );
      
      if (joinCheckError) throw joinCheckError;
      
      if (!canJoin) {
        addToast({
          type: 'error',
          title: t('errors.join_error'),
          description: t('errors.cannot_join_group')
        });
        return;
      }
      
      // Check if user was previously a member
      const { data: existingMembership, error: membershipError } = await supabase
        .from('group_members')
        .select('*')
        .eq('group_id', id)
        .eq('user_id', user.id);
        
      if (membershipError) throw membershipError;
      
      let memberData;
      
      if (existingMembership && existingMembership.length > 0) {
        // Update existing membership to active
        const { data: updatedMember, error: updateError } = await supabase
          .from('group_members')
          .update({ status: 'active' })
          .eq('id', existingMembership[0].id)
          .select('*, profiles(*)')
          .single();
          
        if (updateError) throw updateError;
        memberData = updatedMember;
      } else {
        // Join the group (create new membership)
        const { data: newMember, error: joinError } = await supabase
          .from('group_members')
          .insert({
            group_id: id,
            user_id: user.id,
            role: 'member',
            status: 'active'
          })
          .select('*, profiles(*)')
          .single();
        
        if (joinError) throw joinError;
        memberData = newMember;
      }
      
      // Update UI
      setMembers([...members, memberData]);
      setUserMember(memberData);
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.join_success')
      });
      
    } catch (error) {
      console.error('Error joining group:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.join_group_failed')
      });
    }
  };

  // Handle requesting to join a private/invite-only group
  const handleRequestJoin = async () => {
    if (!user) {
      addToast({
        type: 'error',
        title: t('errors.authentication_required'),
        description: t('errors.login_required_action')
      });
      return;
    }
    
    try {
      // Check if a request already exists
      const { data: existingRequests, error: checkError } = await supabase
        .from('group_join_requests')
        .select('id, status')
        .eq('group_id', id)
        .eq('user_id', user.id);
      
      if (checkError) throw checkError;
      
      // If there's a pending request, don't create a new one
      if (existingRequests && existingRequests.length > 0) {
        const activeRequest = existingRequests.find(req => req.status === 'pending');
        if (activeRequest) {
          addToast({
            type: 'info',
            title: t('info'),
            description: t('groups.request_already_pending')
          });
          return;
        }
      }
      
      // Create the join request
      const { error: requestError } = await supabase
        .from('group_join_requests')
        .insert({
          group_id: id,
          user_id: user.id,
          status: 'pending'
        });
      
      if (requestError) throw requestError;
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.request_sent')
      });
      
    } catch (error) {
      console.error('Error requesting to join group:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.request_join_failed')
      });
    }
  };

  // Handle leaving a group
  const handleLeaveGroup = async () => {
    if (!user || !userMember) {
      return;
    }
    
    // Check if user is the creator
    if (group.creator_id === user.id) {
      addToast({
        type: 'error',
        title: t('errors.leave_error'),
        description: t('errors.creator_cannot_leave')
      });
      return;
    }
    
    try {
      // Update member status to inactive
      const { error: leaveError } = await supabase
        .from('group_members')
        .update({ status: 'inactive' })
        .eq('id', userMember.id);
      
      if (leaveError) throw leaveError;
      
      // Update UI
      setMembers(members.filter(m => m.id !== userMember.id));
      setUserMember(null);
      
      addToast({
        type: 'success',
        title: t('success'),
        description: t('groups.leave_success')
      });
      
    } catch (error) {
      console.error('Error leaving group:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('errors.leave_group_failed')
      });
    }
  };

  // Go back to groups page
  const handleBack = () => {
    navigate('/groups');
  };

  // Create a new ride
  const handleCreateRide = () => {
    navigate(`/groups/${id}/ride/create`);
  };

  // Create a new discussion
  const handleCreateDiscussion = () => {
    console.log(`Navigating to create discussion for group ${id}`);
    navigate(`/groups/${id}/discussion/create`);
  };

  // View a ride
  const handleViewRide = (rideId) => {
    navigate(`/groups/${id}/ride/${rideId}`);
  };

  // View a discussion
  const handleViewDiscussion = (discussionId) => {
    navigate(`/groups/${id}/discussion/${discussionId}`);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Render member role icon
  const renderRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <FaCrown title={t('groups.roles.admin')} />;
      case 'moderator':
        return <FaUserShield title={t('groups.roles.moderator')} />;
      default:
        return <FaUser title={t('groups.roles.member')} />;
    }
  };

  if (loading) {
    return (
      <>
        <div className="groups-page">
          <Spinner size="large" />
        </div>
      </>
    );
  }

  if (!group) {
    return (
      <>
        <div className="groups-page">
          <div className="groups-empty">
            <h3>{t('groups.group_not_found')}</h3>
            <CustomButton2
              label={t('groups.back_to_groups')}
              onClick={handleBack}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="group-details-page">
        <div className="back-button" style={{ marginBottom: '20px' }}>
          <CustomButton8 onClick={handleBack}>
            <FaArrowLeft />
          </CustomButton8>
        </div>
        
        <div className="group-details-header">
          <div 
            className={`group-details-banner ${!group.banner_image_url ? 'no-image' : ''}`}
            style={{
              backgroundImage: group.banner_image_url 
                ? `url(${group.banner_image_url}?t=${Date.now()})` 
                : 'none'
            }}
          />
          <div className="group-details-header-content">
            <h1 className="group-details-title">{group.name}</h1>
            <div className="group-details-meta">
              <div>
                <FaUsers />
                <span>{members.length} {t('groups.members')}</span>
              </div>
              {group.category && (
                <div>
                  <span>{t(`groups.categories.${group.category}`)}</span>
                </div>
              )}
              {group.location && (
                <div>
                  <FaMapMarkerAlt />
                  <span>{group.location}</span>
                </div>
              )}
              <div>
                {group.visibility === 'public' ? (
                  <>
                    <FaGlobe />
                    <span>{t('groups.public')}</span>
                  </>
                ) : group.visibility === 'private' ? (
                  <>
                    <FaLock />
                    <span>{t('groups.private')}</span>
                  </>
                ) : (
                  <>
                    <FaUserPlus />
                    <span>{t('groups.invite_only')}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="group-details-content">
          <div className="group-details-main">
            <div className="group-tabs">
              <div 
                className={`group-tab ${activeTab === 'about' ? 'active' : ''}`}
                onClick={() => setActiveTab('about')}
              >
                {t('groups.about')}
              </div>
              <div 
                className={`group-tab ${activeTab === 'rides' ? 'active' : ''}`}
                onClick={() => setActiveTab('rides')}
              >
                {t('groups.rides')} ({groupRides.length})
              </div>
              <div 
                className={`group-tab ${activeTab === 'discussions' ? 'active' : ''}`}
                onClick={() => setActiveTab('discussions')}
              >
                {t('groups.discussions')} ({discussions.length})
              </div>
              <div 
                className={`group-tab ${activeTab === 'members' ? 'active' : ''}`}
                onClick={() => setActiveTab('members')}
              >
                {t('groups.members')} ({members.length})
              </div>
            </div>
            
            {activeTab === 'about' && (
              <div className="group-about">
                <div className="group-description">
                  {group.description || t('groups.no_description')}
                </div>
                
                {(group.schedule || group.meeting_point) && (
                  <div className="group-info-section">
                    <h3>{t('groups.group_info')}</h3>
                    
                    {group.schedule && (
                      <div className="group-info-item">
                        <FaCalendarAlt />
                        <div>
                          <strong>{t('groups.schedule')}</strong>
                          <p>{group.schedule}</p>
                        </div>
                      </div>
                    )}
                    
                    {group.meeting_point && (
                      <div className="group-info-item">
                        <FaMapMarkerAlt />
                        <div>
                          <strong>{t('groups.meeting_point')}</strong>
                          <p>{group.meeting_point}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'rides' && (
              <div className="group-rides">
                {userMember && (userMember.role === 'admin' || userMember.role === 'moderator') && (
                  <div className="group-action-buttons" style={{ marginBottom: '20px' }}>
                    <CustomButton2
                      label={t('groups.create_ride')}
                      onClick={handleCreateRide}
                      icon={<FaCar />}
                    />
                  </div>
                )}
                
                {groupRides.length === 0 ? (
                  <div className="empty-list-message">
                    <p>{t('groups.no_rides')}</p>
                    {userMember && (
                      <p>{t('groups.create_first_ride')}</p>
                    )}
                  </div>
                ) : (
                  <div className="group-rides-list">
                    {groupRides.map(ride => (
                      <div 
                        key={ride.id} 
                        className="group-list-item"
                        onClick={() => handleViewRide(ride.id)}
                      >
                        <div className="ride-date">
                          <FaCalendarAlt />
                          <span>{formatDate(ride.travel_date)}</span>
                          <span>{ride.travel_time}</span>
                        </div>
                        <div className="ride-info">
                          <h3>{ride.title}</h3>
                          <div className="ride-route">
                            <div className="ride-from">
                              <strong>{t('groups.from')}</strong>: {ride.address_from_city || ride.address_from_street}
                            </div>
                            <div className="ride-to">
                              <strong>{t('groups.to')}</strong>: {ride.address_to_city || ride.address_to_street}
                            </div>
                          </div>
                        </div>
                        <div className="ride-meta">
                          <div className="ride-seats">
                            <FaUsers />
                            <span>{ride.available_seats} / {ride.max_seats}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'discussions' && (
              <div className="group-discussions">
                {userMember && (
                  <div className="group-action-buttons" style={{ marginBottom: '20px' }}>
                    <CustomButton2
                      label={t('groups.create_discussion')}
                      onClick={handleCreateDiscussion}
                      icon={<FaComments />}
                    />
                  </div>
                )}
                
                {discussions.length === 0 ? (
                  <div className="empty-list-message">
                    <p>{t('groups.no_discussions')}</p>
                    {userMember && (
                      <p>{t('groups.create_first_discussion')}</p>
                    )}
                  </div>
                ) : (
                  <div className="group-discussions-list">
                    {discussions.map(discussion => (
                      <div 
                        key={discussion.id} 
                        className="group-list-item"
                        onClick={() => handleViewDiscussion(discussion.id)}
                      >
                        <div className="discussion-info">
                          <h3>{discussion.title}</h3>
                          <p>{discussion.content.substring(0, 100)}...</p>
                        </div>
                        <div className="discussion-meta">
                          <div className="discussion-author">
                            {discussion.profiles?.username || t('groups.unknown_user')}
                          </div>
                          <div className="discussion-date">
                            {formatDate(discussion.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'members' && (
              <div className="group-members">
                <h3>{t('groups.members_list')}</h3>
                <div className="group-members-list">
                  {members.map(member => (
                    <div key={member.id} className="group-member">
                      <div 
                        className="group-member-avatar"
                        style={{
                          backgroundImage: member.profiles?.avatar_url 
                            ? `url(${member.profiles.avatar_url})` 
                            : 'none'
                        }}
                      />
                      <div className="group-member-info">
                        <div className="group-member-name">
                          {member.profiles?.username || t('groups.unknown_user')}
                        </div>
                        <div className="group-member-role">
                          {renderRoleIcon(member.role)}
                          <span>{t(`groups.roles.${member.role}`)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="group-details-sidebar">
            <div className="group-sidebar-section">
              <h3 className="group-sidebar-title">{t('groups.group_actions')}</h3>
              
              {!userMember ? (
                group.visibility === 'public' ? (
                  <CustomButton2
                    label={t('groups.join_group')}
                    onClick={handleJoinGroup}
                    icon={<FaUsers />}
                    fullWidth
                  />
                ) : (
                  <CustomButton2
                    label={t('groups.request_to_join')}
                    onClick={handleRequestJoin}
                    icon={<FaUserPlus />}
                    fullWidth
                  />
                )
              ) : (
                <CustomButton2
                  label={t('groups.leave_group')}
                  onClick={handleLeaveGroup}
                  icon={<FaSignOutAlt />}
                  fullWidth
                />
              )}
            </div>
            
            {userMember && (userMember.role === 'admin' || userMember.role === 'moderator') && (
              <div className="group-sidebar-section">
                <h3 className="group-sidebar-title">{t('groups.management')}</h3>
                <CustomButton2
                  label={t('groups.edit_group')}
                  onClick={() => navigate(`/groups/${id}/edit`)}
                  fullWidth
                />
                {userMember.role === 'admin' && (
                  <>
                    <div style={{ height: '10px' }}></div>
                    <CustomButton2
                      label={t('groups.manage_members')}
                      onClick={() => navigate(`/groups/${id}/members`)}
                      fullWidth
                    />
                  </>
                )}
              </div>
            )}
            
            <div className="group-sidebar-section">
              <h3 className="group-sidebar-title">{t('groups.creator')}</h3>
              <div className="group-creator">
                <div 
                  className="group-creator-avatar"
                  style={{
                    backgroundImage: group.profiles?.avatar_url 
                      ? `url(${group.profiles.avatar_url})` 
                      : 'none'
                  }}
                />
                <div className="group-creator-info">
                  <div className="group-creator-name">
                    {group.profiles?.username || t('groups.unknown_user')}
                  </div>
                  <div className="group-creator-since">
                    {t('groups.created')} {formatDate(group.created_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GroupDetailsPage;
